import React, { useState, useEffect } from 'react';

interface SidebarProps {
  currentPage: string;
  onNavigate: (page: string) => void;
}

const ALL_NAV_ITEMS = [
  { key: 'planner', label: 'Planner', icon: '📋' },
  { key: 'upgrade', label: 'Go Premium', icon: '✨' },
  { key: 'calendar', label: 'Calendar', icon: '📅' },
  { key: 'analytics', label: 'Analytics', icon: '📊' },
  { key: 'habits', label: 'Habits', icon: '✓' },
  { key: 'reflection', label: 'Reflection', icon: '💭' },
  { key: 'meals', label: 'Meals', icon: '🍽️' },
  { key: 'cleaning', label: 'Cleaning', icon: '🧹' },
  { key: 'goals', label: 'Goals', icon: '🎯' },
  { key: 'focus', label: 'Focus Time', icon: '🎧' },
  { key: 'timetracking', label: 'Time Track', icon: '⏱️' },
  { key: 'shopping', label: 'Shopping', icon: '🛒' },
  { key: 'family', label: 'Family', icon: '👨‍👩‍👧' },
  { key: 'budget', label: 'Budget', icon: '💰' },
  { key: 'weather', label: 'Weather', icon: '☀️' },
  { key: 'achievements', label: 'Achievements', icon: '🏆' },
  { key: 'health', label: 'Health', icon: '❤️' },
  { key: 'weighttracker', label: 'Weight', icon: '⚖️' },
  { key: 'travel', label: 'Travel', icon: '✈️' },
  { key: 'calendarsync', label: 'Cal Sync', icon: '🔗' },
  { key: 'workspaces', label: 'Workspaces', icon: '👥' },
  { key: 'locationreminders', label: 'Location', icon: '📍' }
];

const DEFAULT_PINNED = ['planner', 'calendar', 'analytics'];

const Sidebar: React.FC<SidebarProps> = ({ currentPage, onNavigate }) => {
  const [pinnedItems, setPinnedItems] = useState<string[]>(() => {
    const saved = localStorage.getItem('pinnedNavItems');
    return saved ? JSON.parse(saved) : DEFAULT_PINNED;
  });
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showPinMenu, setShowPinMenu] = useState<string | null>(null);

  useEffect(() => {
    localStorage.setItem('pinnedNavItems', JSON.stringify(pinnedItems));
  }, [pinnedItems]);

  const togglePin = (key: string) => {
    if (pinnedItems.includes(key)) {
      // Can't unpin if it's the last one
      if (pinnedItems.length <= 1) return;
      setPinnedItems(pinnedItems.filter(k => k !== key));
    } else {
      // Max 3 pinned
      if (pinnedItems.length >= 3) {
        alert('You can only pin up to 3 items. Unpin one first.');
        return;
      }
      setPinnedItems([...pinnedItems, key]);
    }
    setShowPinMenu(null);
  };

  // Sort items: pinned first (in pin order), then rest alphabetically
  const sortedItems = [
    ...ALL_NAV_ITEMS.filter(item => pinnedItems.includes(item.key))
      .sort((a, b) => pinnedItems.indexOf(a.key) - pinnedItems.indexOf(b.key)),
    ...ALL_NAV_ITEMS.filter(item => !pinnedItems.includes(item.key))
      .sort((a, b) => a.label.localeCompare(b.label))
  ];

  const pinnedCount = pinnedItems.length;

  return (
    <div
      style={{
        width: isCollapsed ? '60px' : '200px',
        height: '100vh',
        backgroundColor: '#1f2937',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.2s ease',
        position: 'fixed',
        left: 0,
        top: 0,
        zIndex: 100,
        boxShadow: '2px 0 10px rgba(0,0,0,0.1)'
      }}
    >
      {/* Logo/Collapse Button */}
      <div style={{
        padding: '16px',
        borderBottom: '1px solid #374151',
        display: 'flex',
        alignItems: 'center',
        justifyContent: isCollapsed ? 'center' : 'space-between'
      }}>
        {!isCollapsed && (
          <span style={{ color: 'white', fontWeight: 700, fontSize: '1.1rem' }}>Yojana</span>
        )}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          style={{
            background: 'none',
            border: 'none',
            color: '#9ca3af',
            cursor: 'pointer',
            fontSize: '1.2rem',
            padding: '4px'
          }}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? '→' : '←'}
        </button>
      </div>

      {/* Navigation Items */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
        {sortedItems.map((item, index) => {
          const isActive = currentPage === item.key;
          const isPinned = pinnedItems.includes(item.key);
          const isLastPinned = isPinned && index === pinnedCount - 1;

          return (
            <React.Fragment key={item.key}>
              <div
                style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center'
                }}
                onMouseEnter={() => setShowPinMenu(item.key)}
                onMouseLeave={() => setShowPinMenu(null)}
              >
                <button
                  onClick={() => onNavigate(item.key)}
                  data-testid={`sidebar-${item.key}`}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: isCollapsed ? '12px' : '12px 16px',
                    justifyContent: isCollapsed ? 'center' : 'flex-start',
                    backgroundColor: isActive ? '#4f46e5' : 'transparent',
                    color: isActive ? 'white' : '#d1d5db',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: isActive ? 600 : 400,
                    transition: 'all 0.15s ease',
                    marginBottom: '4px'
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) e.currentTarget.style.backgroundColor = '#374151';
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  title={isCollapsed ? item.label : undefined}
                >
                  <span style={{ fontSize: '1.1rem' }}>{item.icon}</span>
                  {!isCollapsed && <span>{item.label}</span>}
                  {!isCollapsed && isPinned && (
                    <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: '#fbbf24' }}>📌</span>
                  )}
                </button>

                {/* Pin/Unpin Button (shown on hover) */}
                {!isCollapsed && showPinMenu === item.key && (
                  <button
                    onClick={(e) => { e.stopPropagation(); togglePin(item.key); }}
                    style={{
                      position: 'absolute',
                      right: '4px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: '#374151',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      color: '#d1d5db',
                      fontSize: '0.65rem',
                      cursor: 'pointer',
                      zIndex: 10
                    }}
                    title={isPinned ? 'Unpin' : 'Pin to top'}
                  >
                    {isPinned ? 'Unpin' : 'Pin'}
                  </button>
                )}
              </div>

              {/* Divider after pinned items */}
              {isLastPinned && (
                <div style={{
                  height: '1px',
                  backgroundColor: '#374151',
                  margin: '8px 0'
                }} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Pinning hint */}
      {!isCollapsed && (
        <div style={{
          padding: '12px 16px',
          borderTop: '1px solid #374151',
          color: '#6b7280',
          fontSize: '0.7rem',
          textAlign: 'center'
        }}>
          Hover to pin/unpin (max 3)
        </div>
      )}
    </div>
  );
};

export default Sidebar;
